import React, { useCallback, useEffect, useState } from "react";
import { Clock, Eye, History as HistoryIcon, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const MODEL_LABELS = {
    "sonnet-4-5": "Sonnet 4.5",
    "opus-4-7": "Opus 4.7",
    "gpt-5-4": "GPT-5.4",
};

async function fetchVersions(projectId) {
    const res = await fetch(`${API}/projects/${projectId}/versions`, { credentials: "include" });
    if (!res.ok) throw new Error("Failed to load versions");
    return res.json();
}

async function fetchVersion(projectId, versionId) {
    const res = await fetch(`${API}/projects/${projectId}/versions/${versionId}`, {
        credentials: "include",
    });
    if (!res.ok) throw new Error("Failed to load version");
    return res.json();
}

async function restoreVersion(projectId, versionId) {
    const res = await fetch(`${API}/projects/${projectId}/versions/${versionId}/restore`, {
        method: "POST",
        credentials: "include",
    });
    if (!res.ok) throw new Error("Restore failed");
    return res.json();
}

function relativeTime(iso) {
    const t = new Date(iso).getTime();
    const diff = Math.max(1, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

export default function HistoryDialog({ open, onOpenChange, projectId, currentVersionId, onPreview, onRestore }) {
    const [state, setState] = useState({ loading: true, versions: [], current: null });
    const [busyId, setBusyId] = useState(null);

    const refresh = useCallback(async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const data = await fetchVersions(projectId);
            setState({
                loading: false,
                versions: data.versions || [],
                current: data.current_version_id || currentVersionId,
            });
        } catch (e) {
            toast.error(e.message);
            setState((s) => ({ ...s, loading: false }));
        }
    }, [projectId, currentVersionId]);

    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    const handlePreview = async (v) => {
        setBusyId(`preview-${v.version_id}`);
        try {
            const full = await fetchVersion(projectId, v.version_id);
            onPreview?.(full);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusyId(null);
        }
    };

    const handleRestore = async (v) => {
        setBusyId(`restore-${v.version_id}`);
        try {
            const res = await restoreVersion(projectId, v.version_id);
            onRestore?.(res);
            toast.success(`Restored to version ${v.version_number}`);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="history-dialog"
                className="rounded-none border-foreground max-w-2xl h-[80vh] flex flex-col p-0 gap-0"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <DialogTitle className="tracking-tight text-2xl font-black flex items-center gap-3">
                        <HistoryIcon className="w-5 h-5" strokeWidth={1.5} /> Version history
                    </DialogTitle>
                    <DialogDescription>
                        Every generation is snapshotted. Preview any version in the iframe, or restore it to make it current.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto loom-scroll">
                    {state.loading ? (
                        <div className="p-8 overline">▍ Loading history…</div>
                    ) : state.versions.length === 0 ? (
                        <div className="p-12 text-center">
                            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                ▍ No generations yet — run one to start the log
                            </div>
                        </div>
                    ) : (
                        <ol className="relative">
                            {state.versions.map((v) => {
                                const isCurrent = v.version_id === state.current;
                                return (
                                    <li
                                        key={v.version_id}
                                        data-testid={`version-row-${v.version_id}`}
                                        className={`grid grid-cols-[80px_1fr_auto] gap-4 items-start px-6 py-5 border-b border-border ${
                                            isCurrent ? "bg-secondary" : "hover:bg-secondary/50"
                                        } transition-colors`}
                                    >
                                        <div className="flex flex-col items-start">
                                            <div className="font-black text-3xl leading-none tracking-tighter">
                                                v{v.version_number}
                                            </div>
                                            {isCurrent && (
                                                <span
                                                    data-testid={`version-current-${v.version_id}`}
                                                    className="mt-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] text-accent"
                                                >
                                                    <span className="w-1.5 h-1.5 bg-accent" /> current
                                                </span>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-3">
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                                                    {relativeTime(v.created_at)}
                                                </span>
                                                <span className="border border-border px-1.5 py-0.5">
                                                    {MODEL_LABELS[v.model] || v.model}
                                                </span>
                                            </div>
                                            <div className="mt-2 text-sm leading-relaxed line-clamp-2">
                                                {v.prompt || <span className="text-muted-foreground italic">no prompt</span>}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1.5 items-stretch">
                                            <button
                                                data-testid={`preview-version-${v.version_id}`}
                                                disabled={busyId?.startsWith("preview")}
                                                onClick={() => handlePreview(v)}
                                                className="inline-flex items-center justify-center gap-1.5 h-8 px-3 border border-border font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-foreground hover:text-background hover:border-foreground transition-colors disabled:opacity-40"
                                            >
                                                {busyId === `preview-${v.version_id}` ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                                ) : (
                                                    <Eye className="w-3 h-3" strokeWidth={1.5} />
                                                )}
                                                Preview
                                            </button>
                                            <button
                                                data-testid={`restore-version-${v.version_id}`}
                                                disabled={isCurrent || busyId?.startsWith("restore")}
                                                onClick={() => handleRestore(v)}
                                                className="inline-flex items-center justify-center gap-1.5 h-8 px-3 border border-foreground bg-foreground text-background font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-accent hover:border-accent transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-foreground disabled:hover:border-foreground"
                                            >
                                                {busyId === `restore-${v.version_id}` ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                                ) : (
                                                    <RotateCcw className="w-3 h-3" strokeWidth={1.5} />
                                                )}
                                                Restore
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
